import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RelationshipStatusSubmission } from '../../../../../models/submissions/relationship-status-submission';
import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import * as moment from 'moment';
import { RelationshipStatusSubmissionDetailsUtils } from '../../../../../utils/relationship-status-submission-details-utils';

@Component({
  selector: 'app-relationship-status-changes',
  templateUrl: './relationship-status-changes.component.html',
  styleUrls: ['./relationship-status-changes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelationshipStatusChangesComponent implements OnChanges {
  @Input() submission!: RelationshipStatusSubmission;

  private _submission = new BehaviorSubject<RelationshipStatusSubmission | null>(null);
  public submission$ = this._submission.pipe(filter((sub): sub is RelationshipStatusSubmission => sub !== null));

  public submissionInfoItems$ = this.submission$.pipe(
    map(sub => this.createSubmissionInfoItemArrays(sub)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public previousInfo$ = this.submissionInfoItems$.pipe(map(info => info.previous));
  public newInfo$ = this.submissionInfoItems$.pipe(map(info => info.new));

  public previousInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      return !sub.previous ? $localize`Not Applicable` : '';
    })
  );

  public newInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      return !RelationshipStatusSubmissionDetailsUtils.relationshipStatusChanged(sub) ? $localize`No Changes` : '';
    })
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.submission) {
      this._submission.next(this.submission);
    }
  }

  private createSubmissionInfoItemArrays(submission: RelationshipStatusSubmission) {
    const previousData = submission.previous;
    const newData = submission.new;

    const items = [
      {
        label: $localize`Relationship Status`,
        prev: previousData?.relationshipStatus?.name,
        new: newData?.relationshipStatus?.name
      },
      {
        label: $localize`Relationship Effective Date`,
        prev: previousData?.effectiveDate ? moment.utc(previousData.effectiveDate).format('MMM D, YYYY') : '',
        new: newData?.effectiveDate ? moment.utc(newData.effectiveDate).format('MMM D, YYYY') : ''
      }
    ];

    return {
      previous: items.map(item => ({
        label: item.label,
        value: item.prev || '',
        hasChanged: item.prev !== item.new
      })),
      new: items.map(item => ({
        label: item.label,
        value: item.new || '',
        hasChanged: item.prev !== item.new
      }))
    };
  }
}
